import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { L10nTranslationService } from 'angular-l10n';
import { SnackbarService } from '../shared/snackbar.service';
import { catchError, map, Observable, of } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { ApiError } from '../shared/model/utility';
import { IdentityProviderModel, IdentityProviderModelListApiResponse, IdentityProvidersApi, Metadata, SyncProvidersForTenantRequest, TenantApi, UpdateMetadataForTenantProviderRequest } from '../hickory-api';
import { AuthService } from '../shared/authentication/auth.service';
import { AccessControl, AuthZAccessControl, IDENTITY_PROVIDERS_AUTHZ_DOMAIN } from '../shared/authentication/access-control';
import { SelectedIdentityProviderContext, IdentityProviderTableContext, IdpSelectionResult, IdpModelWithSummary } from './models';

@Injectable({
  providedIn: 'root',
})
export class IdentityProvidersHelperService {

  public readonly authzDomain = IDENTITY_PROVIDERS_AUTHZ_DOMAIN;
  public static readonly USERNAME_PASSWORD_SCHEME = 'local';
  public static readonly USERNAME_PASSWORD_IDP_STRING = `provider=${IdentityProvidersHelperService.USERNAME_PASSWORD_SCHEME}`;

  private readonly sb = inject(SnackbarService);
  private readonly router = inject(Router);
  private readonly providersApi = inject(IdentityProvidersApi);
  private readonly translation = inject(L10nTranslationService);
  private readonly authService = inject(AuthService);
  private readonly tenantApi = inject(TenantApi);

  public static readonly fullIdpCompare = (option: IdpSelectionResult, value: IdpSelectionResult) => option?.id === value?.id && option?.scheme === value?.scheme;
  public static idpStringCompare(option: string, value: string) {
    const aScheme = IdentityProvidersHelperService.getProviderSchemeFromString(option);
    const bScheme = IdentityProvidersHelperService.getProviderSchemeFromString(value);
    return (
      aScheme === bScheme ||
      (aScheme === IdentityProvidersHelperService.USERNAME_PASSWORD_SCHEME && !bScheme) ||
      (bScheme === IdentityProvidersHelperService.USERNAME_PASSWORD_SCHEME && !aScheme)
    );
  }

  public static getProviderSchemeFromString(s: string) {
    if (!s) return null;
    if (s.includes('provider=')) {
      const split = s.split(';');
      const providerPart = split[0];
      return providerPart.replace('provider=', '');
    } else {
      return s;
    }
  }

  public static toLegacyIdpString(scheme: string) {
    if (!scheme) return null;
    return `provider=${scheme}`;
  }

  public static isUsernameAndPasswordProvider(idpScheme: string): boolean {
    return !idpScheme || this.getProviderSchemeFromString(idpScheme) === IdentityProvidersHelperService.USERNAME_PASSWORD_SCHEME;
  }

  getTenantFeatureConfig(tenantId: number): Observable<Partial<IdentityProviderTableContext>> {
    const isSuperAdmin = this.authService.isSuperAdmin();
    if (this.authService.isSuperAdmin() && tenantId > 0) {
      return this.tenantApi.listTenantFeatureToggles(tenantId, true).pipe(
        // the PermissionKey is defined as camelCase, but the API returns it as PascalCase
        map(res => ({
          tenantId,
          isSuperAdmin,
          featureEnabled: res.items?.some(toggle => (<unknown>toggle.featureToggle)['PermissionKey'] === AccessControl.EnabledIdentityProviders && toggle.isEnabled),
          accessControlEnabled: res.items?.some(toggle => (<unknown>toggle.featureToggle)['PermissionKey'] === AuthZAccessControl.FeatureIdentityProvidersAuthZ && toggle.isEnabled)
        }))
      );
    } else {
      return of({
        tenantId,
        isSuperAdmin,
        featureEnabled: this.authService.hasPermission(AccessControl.EnabledIdentityProviders),
        accessControlEnabled: AuthZAccessControl.canAccessAuthZDomain(this.authService, this.authzDomain)
      })
    }
  }

  getTenantIdpSummary(tenantId: number) {
    return this.providersApi.getTenantIdentityProvidersSummaries(tenantId)
      .pipe(
        map(r => r?.items),
        catchError(err => this.onCatchError(err, 'IdentityProviders.GetTenantProvidersError'))
      );
  }

  getSelectedProviderContext(tenantId: number, provider: IdpModelWithSummary): Observable<SelectedIdentityProviderContext> {
    return this.providersApi.getProviderById(tenantId, provider.id).pipe(
      map(res => {
        const selected = res.item;
        const defaultMetadata: Metadata = {
          tenantId: tenantId.toString(),
          ownerId: this.authService.isSuperAdmin() ? null : this.authService.user?.snapshot?.id?.toString(),
          tags: {},
          attributes: {},
          statistics: {},
          directoryOwner: null,
          featuresInUse: null
        }

        selected.metadata = {
          ...defaultMetadata,
          ...provider.metadata
        };

        return new SelectedIdentityProviderContext(this.authService, tenantId, { ...selected, ...provider });
      })
    );
  }

  updateProviderMetadata(context: SelectedIdentityProviderContext, cb: () => void) {

    if (!context.validForUpdate()) {
      return;
    }

    const { tenantId, provider } = context;

    const request: UpdateMetadataForTenantProviderRequest = {
      tenantId: tenantId,
      providerId: provider.id,
      updatedMetadata: provider.metadata
    };

    this.providersApi.updateMetadataForTenantProvider(tenantId, provider.id, request)
      .subscribe({
        next: _ => {
          this.onRequestSuccess('IdentityProviders.UpdateMetadataSuccess', false, { tenantId });
          cb();
        },
        error: err => this.onRequestError(err, 'IdentityProviders.UpdateMetadataError')
      });
  }

  syncForTenant(tenantId: number, allowedProviderIds: number[]) {
    const request: SyncProvidersForTenantRequest = {
      tenantId,
      allowedProviders: allowedProviderIds
    }
    this.providersApi.syncProvidersForTenant(tenantId, request)
      .subscribe({
        next: _ => this.onRequestSuccess('IdentityProviders.SyncTenantSuccess', true, { tenantId }),
        error: err => this.onRequestError(err, 'IdentityProviders.SyncTenantError')
      });
  }

  tenantProviders(tenantId: number) {
    return this.providersApi.getProvidersForTenant(tenantId)
      .pipe(
        map(r => r?.items),
        catchError(err => this.onCatchError(err, 'IdentityProviders.GetTenantProvidersError'))
      );
  }

  tenantProvidersSignal(tenantId: number) {
    return toSignal<IdentityProviderModel[]>(this.tenantProviders(tenantId));
  }

  getSystemProviders() {
    return this.providersApi.getAllIdentityProviders()
      .pipe(
        map(r => r?.items),
        catchError(err => this.onCatchError(err, 'IdentityProviders.GetSystemProvidersError'))
      );
  }

  syncForSystem(cb: () => void) {
    this.providersApi.syncProvidersForSystem()
      .subscribe({
        next: _ => {
          this.onRequestSuccess('IdentityProviders.SyncSystemSuccess', false);
          cb();
        },
        error: err => {
          this.onRequestError(err, 'IdentityProviders.SyncSystemError');
        }
      });
  }

  systemProvidersSignal<T>(mapper: (response: IdentityProviderModelListApiResponse) => T[]) {
    return toSignal<T[]>(
      this.providersApi.getAllIdentityProviders()
        .pipe(
          map(mapper),
          catchError(err => this.onCatchError(err, 'IdentityProviders.GetSystemProvidersError'))
        ));
  }

  onRequestSuccess(localizationKey: string, backToListView: boolean, queryParams: unknown = {}) {
    const message = this.translation.translate(localizationKey);
    this.sb.open(message);
    if (backToListView) {
      void this.router.navigate(['/identity-providers'], { queryParams, queryParamsHandling: 'merge' });
    }
  }

  onRequestError(error: ApiError, localizationKey: string) {
    const errorMessage = this.translation.translate(localizationKey, { errorMessage: error.message });
    this.sb.error(errorMessage);
  }

  private onCatchError(error: ApiError, localizationKey: string) {
    this.onRequestError(error, localizationKey);
    return of(null);
  }
}
